import { useApiClient } from '../../api/useApiClient';

/**
 * Timeout dedicado (ms) para las llamadas de firma masiva (#103.1). El axios global usa 10s
 * (useApiClient.js); bajo carga next-chunk/submit-chunk pueden tardar más server-side y abortar
 * con ECONNABORTED. Se alinea al connectionTimeout de Hikari (30s) para no cortar picos transitorios.
 */
const SIGN_API_TIMEOUT_MS = 30000;

/** Reintentos máximos ante timeout (además del intento inicial). */
const SIGN_API_MAX_RETRIES = 2;

const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isTimeoutError = (err) =>
    (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) && !err?.response;

/**
 * Token de entrega por intento lógico de next-chunk (#103.2 Task B): UUID que el server usa como
 * clave de idempotencia (el retry con el MISMO token recibe el MISMO set de docs, sin claim doble).
 * Fallback UUID v4 con Math.random para entornos sin crypto.randomUUID (la API exige formato UUID).
 */
const newDeliveryToken = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

/**
 * Servicio del flujo de firma individual server-side (#59, F1C).
 *
 * Cubre la bandeja del firmante y las acciones del documento contra la API de Bóveda:
 * listar pendientes, start (lock + versión congelada), precheck del certificado, confirm del PDF
 * firmado, cancel y reject. La comunicación con el cliente local de firma (daemon PKCS#11) vive
 * en un servicio aparte; acá solo se habla con la API.
 */
class SignatureService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    // ==================== Bandeja ====================

    /**
     * Documentos pendientes de firma visibles para el firmante (ROLE_SIGNER ∩ ACL).
     * Mismo contrato que las bandejas de revisión/aprobación.
     * @param {{page:number,size:number}} pagination
     * @param {Object} filters
     * @param {Array} sort
     */
    async getPendingSignature(pagination = { page: 0, size: 10 }, filters = {}, sort = []) {
        const { page, size } = pagination;
        const payload = { ...filters, sort };
        return this.apiClient.post(
            `/api/v1/docs/pending/signature`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Cantidad de documentos pendientes de firma (para el badge del menú). Pide una página mínima
     * y devuelve el {@code total}. Best-effort: devuelve 0 ante cualquier error.
     */
    async getPendingSignatureCount() {
        try {
            const res = await this.getPendingSignature({ page: 0, size: 1 });
            return res?.data?.total ?? 0;
        } catch {
            return 0;
        }
    }

    // ==================== Acciones del documento ====================

    /**
     * Inicia la firma: adquiere el lock, arma el lote de uno y prepara el placeholder (#108).
     *
     * El documento ya NO se descarga: vuelve el digest del ByteRange, que es lo único que el token
     * necesita firmar. Con eso desaparecen de raíz el timeout de la descarga (#125 / GitLab #40) y
     * el 413 del daemon, que rechazaba cuerpos de más de 1 MB.
     *
     * @returns {Promise} { lockToken, batchId, messageDigestHex, signingDate, padesLevel,
     *                      certMatchStrategy, idDoc, filename, contentType }
     */
    async startSignature(idDoc) {
        return this.apiClient.post(`/api/v1/signatures/individual/${idDoc}/start`);
    }

    /**
     * Confirma la firma: envía el CMS que produjo el token (octet-stream) + Idempotency-Key +
     * lockToken + el lote de uno que devolvió el start (#108).
     *
     * Antes viajaba el PDF firmado entero de vuelta; ahora son unos pocos KB. El servidor ensambla
     * el CMS sobre el placeholder que ya preparó, así que el binario nunca sale de la Bóveda.
     *
     * @param {ArrayBuffer|Uint8Array|Blob} signedCms bytes del CMS externo
     * @param {{idempotencyKey:string, lockToken:string, batchId:number}} opts
     */
    async confirmSignature(idDoc, signedCms, { idempotencyKey, lockToken, batchId }) {
        return this.apiClient.post(
            `/api/v1/signatures/individual/${idDoc}/confirm`,
            signedCms,
            {
                params: { lockToken, batchId },
                // El confirm ensambla, valida la firma y consulta revocación online dentro del
                // request: con el tope global de 10s el navegador abortaba mientras el servidor
                // publicaba, y el usuario veía un error sobre un documento que quedaba firmado.
                // Mismo tope que la firma masiva, que hace exactamente el mismo trabajo.
                timeout: SIGN_API_TIMEOUT_MS,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Idempotency-Key': idempotencyKey,
                },
            }
        );
    }

    /**
     * Cancela la firma en curso: libera el lock, el documento sigue pendiente de firma.
     */
    async cancelSignature(idDoc, lockToken) {
        return this.apiClient.post(
            `/api/v1/signatures/individual/${idDoc}/cancel`,
            null,
            { params: { lockToken } }
        );
    }

    /**
     * Rechaza la firma: transiciona el documento a REJECTED con un motivo. Requiere el lock vigente.
     * @param {string} motivo razón del rechazo (obligatoria)
     */
    async rejectSignature(idDoc, motivo) {
        // Sin lockToken (#108): el server lo toma y lo libera. Antes había que conseguirlo llamando
        // a startSignature —el endpoint de la firma— sólo para rechazar.
        return this.apiClient.post(
            `/api/v1/signatures/individual/${idDoc}/reject`,
            { motivo }
        );
    }

    // ==================== Firma masiva (F1D-5) ====================

    /**
     * Ejecuta {@code fn} con retry acotado SOLO ante timeout (#103.1, resiliencia no correctitud).
     * Backoff lineal (`backoffMs * intento`). Cualquier error que NO sea timeout (4xx/5xx reales,
     * red) se relanza de inmediato sin reintentar. Agotados los reintentos, relanza el último error.
     * Cada retry deja rastro con console.warn (#103.2 E': observabilidad del cliente).
     * @param {() => Promise} fn llamada a envolver
     * @param {{retries?:number, backoffMs?:number, sleep?:Function, label?:string}} opts
     */
    async withTimeoutRetry(fn, { retries = SIGN_API_MAX_RETRIES, backoffMs = 1000, sleep = defaultSleep, label = 'sign-api' } = {}) {
        let lastErr;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await fn();
            } catch (err) {
                if (!isTimeoutError(err)) throw err;
                lastErr = err;
                if (attempt < retries) {
                    console.warn(`${label}: timeout — reintento ${attempt + 1}/${retries}`, err?.message || err);
                    await sleep(backoffMs * (attempt + 1));
                }
            }
        }
        throw lastErr;
    }

    /**
     * Crea un lote de firma masiva. Uno de los dos: {@code docIds} (selección) o {@code filter}
     * (SearchDocsRequest, el server resuelve los SIGNATURE_PENDING con ACL).
     * @param {{docIds?:number[], filter?:object}} sel
     * @returns {Promise} { batchId, totalDocuments }
     */
    async createBatch({ docIds, filter } = {}) {
        const body = (docIds && docIds.length) ? { docIds } : { filter };
        return this.apiClient.post('/api/v1/signatures/batches', body);
    }

    /**
     * Prepara y devuelve el próximo chunk de digests a firmar.
     * @returns {Promise} { chunkDigests:[{docId,messageDigestHex,signingDate,deliveryToken}],
     *   remaining, preparing, batchDone, ingestState }
     *   (#103, ADR-036: `deliveryToken` es obligatorio en submitChunk; `batchDone` indica
     *   que el lote terminó —vacío+batchDone=true= corte; vacío+false= preparer/ingestor activos→ poll)
     */
    async nextChunk(batchId, size, retryFailed = false, sleep) {
        // Timeout dedicado + retry acotado ante ECONNABORTED (#103.1). Idempotencia (#103.2 Task B):
        // se genera UN deliveryToken por intento lógico y el retry reusa EL MISMO — si el claim del
        // intento timeouteado SÍ commiteó server-side, el server renueva el lease y re-entrega el
        // MISMO set (antes reclamaba OTRO set y el primero quedaba leaseado huérfano → freeze).
        const deliveryToken = newDeliveryToken();
        return this.withTimeoutRetry(() => this.apiClient.post(
            `/api/v1/signatures/batches/${batchId}/next-chunk`, null,
            { params: { size, retryFailed, deliveryToken }, timeout: SIGN_API_TIMEOUT_MS }
        ), { sleep, label: `nextChunk(batch=${batchId})` });
    }

    /**
     * Envía los CMS firmados de un chunk para ensamblado async, junto con las fallas por-doc del
     * daemon (#103.2 Task G): un digest que el token no pudo firmar se reporta como
     * {docId, reason} y el server lo marca FAILED(DAEMON_*) — antes esas fallas vivían sólo en la
     * memoria del navegador y el doc quedaba PREPARED fantasma.
     * @param {Array<{docId:number, cmsBase64:string, deliveryToken:string}>} signatures
     *   El backend RECHAZA signatures con deliveryToken nulo/vacío (#103, ADR-036).
     * @param {Array<{docId:number, reason:string}>} [failures] fallas del daemon del MISMO chunk.
     *   Ambas listas son opcionales pero al menos una debe ir no vacía (el server responde 400).
     * @returns {Promise} 202 { batchId, taskId }
     */
    async submitChunk(batchId, signatures, failures = [], sleep) {
        // Timeout dedicado + retry acotado ante ECONNABORTED (#103.1). Idempotente: el backend valida
        // delivery_token + guard de markSigned, un submit reintentado no duplica firmas (#103, ADR-036)
        // y las failures re-enviadas son no-op sobre docs ya FAILED (#103.2 Task G).
        const body = (failures && failures.length > 0) ? { signatures, failures } : { signatures };
        return this.withTimeoutRetry(() => this.apiClient.post(
            `/api/v1/signatures/batches/${batchId}/submit-chunk`, body,
            { timeout: SIGN_API_TIMEOUT_MS }
        ), { sleep, label: `submitChunk(batch=${batchId})` });
    }

    /**
     * Estado detallado del lote (contadores por estado).
     * @returns {Promise} { state, totalDocuments, pending, prepared, signed, assembled, failed }
     */
    async getBatchStatus(batchId) {
        return this.apiClient.get(`/api/v1/signatures/batches/${batchId}`);
    }

    /**
     * Lotes activos (reanudables) del firmante.
     * @returns {Promise} [{ batchId, state, totalDocuments, signedOkCount, signedFailedCount, startedAt }]
     */
    async listActiveBatches() {
        return this.apiClient.get('/api/v1/signatures/batches', { params: { active: true } });
    }

    /**
     * Cancela un lote de firma masiva (pasa a estado CANCELLED).
     * @returns {Promise} BatchStatusResponse con state=CANCELLED
     */
    async cancelBatch(batchId) {
        return this.apiClient.post(`/api/v1/signatures/batches/${batchId}/cancel`);
    }
}

/**
 * Hook para usar el servicio de firma.
 * @returns {SignatureService}
 */
export const useSignatureService = () => {
    const apiClient = useApiClient();
    return new SignatureService(apiClient);
};

export default SignatureService;
